import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { fetchManifests, type ThemeDemo } from './Customizer';

interface DemoImporterProps {
  packSlug: string | null;
  themeSlug: string;
  frontendUrl: string;
}

export function DemoImporter({ packSlug, themeSlug, frontendUrl }: DemoImporterProps) {
  const qc = useQueryClient();
  const [importing, setImporting] = useState<string | null>(null);

  const { data: manifests, isLoading, isError } = useQuery({
    queryKey: ['theme-packs'],
    queryFn: () => fetchManifests(frontendUrl),
  });

  const manifest = useMemo(
    () => manifests?.find((m) => m.slug === (packSlug ?? themeSlug)) ?? null,
    [manifests, packSlug, themeSlug],
  );

  async function importDemo(demo: ThemeDemo) {
    setImporting(demo.slug);
    let ok = 0;
    try {
      for (const page of demo.pages) {
        await api.post('/pages/import', {
          page: { slug: page.slug, locale: page.locale ?? 'es', title: page.title },
          blocks: page.blocks.map((b, i) => ({ type: b.type, order: b.order ?? i, data: b.data })),
        });
        ok += 1;
      }
      toast.success(`Demo «${demo.name}» importada: ${ok} página(s) creada(s) como borrador.`);
      void qc.invalidateQueries({ queryKey: ['pages'] });
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(`Se importaron ${ok}/${demo.pages.length}. ${msg ?? 'Error al importar la demo.'}`);
    } finally {
      setImporting(null);
    }
  }

  if (isLoading) return <p className="text-sm text-neutral-500">Cargando demos del pack…</p>;
  if (isError) {
    return (
      <p className="text-sm text-amber-600">
        No se pudo conectar con el front ({frontendUrl}). Comprueba que está en marcha.
      </p>
    );
  }
  if (!manifest || manifest.demos.length === 0) {
    return <p className="text-sm text-neutral-500">Este pack no incluye demos importables.</p>;
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-neutral-500">
        Importa contenido de ejemplo en el CMS. Las páginas se crean como <strong>borrador</strong> (con slug
        único si ya existe), listas para editar y publicar.
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        {manifest.demos.map((demo) => (
          <div key={demo.slug} className="flex flex-col overflow-hidden rounded-lg border border-neutral-200">
            <div className="aspect-[16/9] w-full bg-neutral-100">
              {demo.screenshot ? (
                <img src={demo.screenshot} alt={demo.name} className="size-full object-cover" />
              ) : (
                <div className="grid size-full place-items-center text-neutral-300">
                  <FileText className="size-10" />
                </div>
              )}
            </div>
            <div className="flex flex-1 flex-col p-3">
              <div className="mb-1 flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold">{demo.name}</h4>
                <span className="text-[11px] text-neutral-400">{demo.pages.length} pág.</span>
              </div>
              {demo.description && <p className="mb-3 line-clamp-2 text-xs text-neutral-500">{demo.description}</p>}
              <div className="mt-auto flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-2 text-xs"
                  onClick={() => window.open(`${frontendUrl}/preview/${themeSlug}/${demo.pages[0]?.slug ?? ''}`, '_blank')}
                  title="Previsualizar (importa primero para verla con contenido real)"
                >
                  <Eye className="size-3.5" />
                </Button>
                <Button
                  size="sm"
                  className="h-8 flex-1 text-xs"
                  disabled={importing !== null}
                  onClick={() => importDemo(demo)}
                >
                  {importing === demo.slug ? (
                    <><Loader2 className="size-3.5 animate-spin" /> Importando…</>
                  ) : (
                    <><Download className="size-3.5" /> Importar demo</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
